import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Newspaper, Loader2, CalendarDays } from "lucide-react";

export default function NewsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const { t, tr } = useLang();

  useEffect(() => { api.get("/public/news").then((r) => setItems(r.data)).finally(() => setLoading(false)); }, []);

  return (
    <div data-testid="public-news-page">
      <section className="bg-[#0E2916] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C59B27] font-mono">{t("Updates")}</p>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold mt-2">{t("News & Announcements")}</h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#C59B27]" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-[#5C7264]"><Newspaper className="h-12 w-12 mx-auto mb-4 text-[#CBD2CC]" /><p>{t("No news published yet.")}</p></div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((n) => (
              <button key={n.id} onClick={() => setSelected(n)} className="text-left bg-white rounded-xl border border-[#E2DAD0] overflow-hidden hover:-translate-y-1 hover:shadow-md hover:border-[#C59B27] transition-all" data-testid={`public-news-${n.id}`}>
                {n.image_path && <div className="h-44 bg-[#E8F0EA]"><img src={fileUrl(n.image_path)} alt={n.title} className="h-full w-full object-cover" /></div>}
                <div className="p-5">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-[#F9F3E3] text-[#A8811A] border-0 text-xs">{n.category}</Badge>
                    {n.date && <span className="text-xs text-[#5C7264] flex items-center gap-1"><CalendarDays className="h-3 w-3" />{n.date}</span>}
                  </div>
                  <h3 className="font-display text-xl font-semibold text-[#194324] mt-2">{tr(n, "title")}</h3>
                  <p className="text-sm text-[#3A4E41] mt-2 line-clamp-3">{n.excerpt || n.content}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl text-[#194324]">{tr(selected, "title")}</DialogTitle></DialogHeader>
              <div className="flex items-center gap-2 mb-1">
                <Badge className="bg-[#F9F3E3] text-[#A8811A] border-0 text-xs">{selected.category}</Badge>
                {selected.date && <span className="text-xs text-[#5C7264]">{selected.date}</span>}
              </div>
              {selected.image_path && <img src={fileUrl(selected.image_path)} alt={selected.title} className="rounded-lg w-full h-56 object-cover" />}
              <p className="text-[#3A4E41] leading-relaxed whitespace-pre-line mt-2">{selected.content || selected.excerpt}</p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
